import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
} from 'react'
import { Check } from '../../../shared/src/data/check'
import { FileDetails } from '../../../shared/src/data/fileDetails'
import { useGetCheck } from './queries/checks'
import { ScanDetails } from '../../../shared/src/data/scanDetails'
import { useGetFilesRelatedToScan } from '../steps/scanMetadataSelection/queries/scanMetadata'
import { useFetchFromServer } from '../api/fetchHook'
import { serverRoutes } from '../layout/serverRoutes'

export interface FileInfo extends FileDetails {
  order: number
  thumbnail?: string
}

export interface AnalysisCheck extends Check {
  checked: boolean
}

interface AnalysisContextType {
  files: FileInfo[]
  analysisChecks: AnalysisCheck[]
  addFiles: (files: FileInfo[]) => void
  updateFile: (id: string, updatedFile: Partial<FileInfo>) => void
  toggleCheck: (id: string, checked: boolean) => void
  setSelectedScan: (scan?: ScanDetails) => void
  selectedScan?: ScanDetails
  canSubmit: boolean
  submit: () => void
  modalState: {
    open: boolean
    status: 'loading' | 'success' | 'error' | null
    message?: string
  }
  closeModal: () => void
}

const AnalysisContext = createContext<AnalysisContextType | undefined>(
  undefined,
)

export const AnalysisProvider = ({ children }: { children: ReactNode }) => {
  const [filesMap, setFilesMap] = useState<Map<string, FileInfo>>(new Map())
  const [analysisChecks, setAnalysisChecks] = useState<AnalysisCheck[]>([])
  const [selectedScan, setSelectedScan] = useState<ScanDetails | undefined>()
  const [modalState, setModalState] = useState<{
    open: boolean
    status: 'loading' | 'success' | 'error' | null
    message?: string
  }>({
    open: false,
    status: null,
  })

  // Fetch checks data
  const { data: fetchedChecks, isSuccess } = useGetCheck()
  useEffect(() => {
    if (isSuccess && fetchedChecks) {
      setAnalysisChecks(
        fetchedChecks
          .map((check: Check) => ({
            ...check,
            checked: true,
          }))
          .filter((check) => check.is_fasttrack),
      )
    }
  }, [isSuccess, fetchedChecks])

  // Fetch files when a new scan is selected
  const { data: relatedFileDetails } = useGetFilesRelatedToScan(
    selectedScan?.uploadId,
  )

  useEffect(() => {
    if (relatedFileDetails) {
      const newFilesMap = new Map<string, FileInfo>()
      relatedFileDetails.forEach((file, index) => {
        newFilesMap.set(file.id, {
          ...file,
          order: index,
        })
      })
      setFilesMap(newFilesMap)
    }
  }, [relatedFileDetails])

  const addFiles = useCallback(
    (files: FileInfo[]) => {
      const newMap = new Map(filesMap)
      files.forEach((file) => {
        newMap.set(file.id, file)
      })
      setFilesMap(newMap)
    },
    [filesMap],
  )

  const updateFile = useCallback(
    (id: string, updatedFile: Partial<FileInfo>) => {
      setFilesMap((prev) => {
        const newMap = new Map(prev)
        const file = newMap.get(id)
        if (file) {
          newMap.set(id, { ...file, ...updatedFile })
        }
        return newMap
      })
    },
    [],
  )

  const filesArray = Array.from(filesMap.values()).sort(
    (a, b) => a.order - b.order,
  )

  const toggleCheck = useCallback((id: string, checked: boolean) => {
    setAnalysisChecks((prev) => {
      const newChecks = [...prev]
      const check = newChecks.find((c) => c.id === id)
      if (check) {
        check.checked = checked
      }
      return newChecks
    })
  }, [])

  const canSubmit = useMemo(() => {
    return filesArray.length !== 0 && selectedScan != null
  }, [filesArray, selectedScan])

  const { fetchJsonFromServer } = useFetchFromServer()
  const submit = useCallback(async () => {
    if (!canSubmit || !selectedScan) return
    setModalState({ open: true, status: 'loading' })

    const requestBody = {
      scan_id: selectedScan.id,
      files_order: Object.fromEntries(
        filesArray.map((file) => [file.id, file.order]),
      ),
      checks: analysisChecks
        .filter((check) => check.checked)
        .map((check) => check.id),
    }

    try {
      console.log('Submitting request:', requestBody)

      const response = await fetchJsonFromServer(
        serverRoutes.api.fasttrack.execute(),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
      )

      console.log('Execution started successfully:', response)

      setModalState({
        open: true,
        status: 'success',
        message:
          'Analysis started successfully! Expect an email when it completes.',
      })

      setTimeout(() => setModalState({ open: false, status: null }), 5000)
    } catch (error) {
      setModalState({
        open: true,
        status: 'error',
        message: 'Submission failed. Please try again later.',
      })
      console.error('Failed to submit scan for execution:', error)
    }
  }, [canSubmit, selectedScan, filesArray, analysisChecks, fetchJsonFromServer])

  const closeModal = useCallback(() => {
    setModalState({ open: false, status: null })
  }, [])

  const contextValue = useMemo(
    () => ({
      files: filesArray,
      analysisChecks,
      addFiles,
      updateFile,
      toggleCheck,
      setSelectedScan,
      selectedScan,
      canSubmit,
      submit,
      modalState,
      closeModal,
    }),
    [
      filesArray,
      analysisChecks,
      addFiles,
      updateFile,
      toggleCheck,
      setSelectedScan,
      selectedScan,
      canSubmit,
      submit,
      modalState,
      closeModal,
    ],
  )

  return (
    <AnalysisContext.Provider value={contextValue}>
      {children}
    </AnalysisContext.Provider>
  )
}

export const useAnalysisContext = (): AnalysisContextType => {
  const context = useContext(AnalysisContext)
  if (!context) {
    throw new Error('useAnalysisContext must be used within a AnalysisProvider')
  }
  return context
}
